import { useEffect, useCallback } from 'react';
import {
  X,
  Keyboard,
  AlertCircle,
  Info,
  Move,
  MousePointerClick,
  Compass,
  ZoomIn,
  Edit3,
  Command,
  Monitor,
  Eye,
  Code2,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../hooks/auth';
import { permissionService } from '../services/permissionService';
import { useStore } from '../store';
import {
  getShortcutsByCategory,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  CATEGORY_COLORS,
  ShortcutCategory,
  type ShortcutDefinition,
} from '../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

// Role configuration with icons and colors
const ROLE_CONFIG = {
  analyst: {
    icon: Sparkles,
    color: 'primary',
    label: 'Аналітик',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/30',
    textColor: 'text-primary',
    iconBgColor: 'bg-primary/20',
  },
  developer: {
    icon: Code2,
    color: 'blue',
    label: 'Розробник',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-500',
    iconBgColor: 'bg-blue-500/20',
  },
  viewer: {
    icon: Eye,
    color: 'gray',
    label: 'Глядач',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/30',
    textColor: 'text-gray-500',
    iconBgColor: 'bg-gray-500/20',
  },
} as const;

type UserRole = keyof typeof ROLE_CONFIG;

export default function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Get user role
  const { user } = useAuth();
  const currentApp = useStore((state) => state.currentApp);
  const userRole = user && currentApp ? permissionService.getEffectiveRole(user, currentApp).role : null;
  const isAnalyst = userRole === 'analyst';

  // Categories to display
  const categories: ShortcutCategory[] = ['navigation', 'editing', 'zoom'];

  // Get all shortcuts grouped by category
  const groupedShortcuts = categories.map((category) => ({
    category,
    shortcuts: getShortcutsByCategory(category),
  }));

  if (!isOpen) return null;

  const roleConfig = userRole ? ROLE_CONFIG[userRole as UserRole] : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-elevated/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary/30 to-primary/10 rounded-xl flex items-center justify-center shadow-lg shadow-primary/10">
              <Keyboard size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Гарячі клавіші</h2>
              <p className="text-xs text-text-muted">Швидкий доступ до функцій</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-elevated rounded-xl transition-all duration-200 hover:scale-105 group"
            title="Закрити (Esc)"
          >
            <X size={20} className="text-text-muted group-hover:text-text-primary transition-colors" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[80vh] overflow-y-auto p-4 space-y-4">
          {/* Role Information Card */}
          {roleConfig && (
            <div
              className={`${roleConfig.bgColor} border ${roleConfig.borderColor} rounded-xl p-3`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-8 h-8 ${roleConfig.iconBgColor} rounded-lg flex items-center justify-center`}
                >
                  <roleConfig.icon size={18} className={roleConfig.textColor} />
                </div>
                <div>
                  <h3
                    className={`text-xs font-semibold ${roleConfig.textColor} uppercase tracking-wider`}
                  >
                    Ваша роль: {roleConfig.label}
                  </h3>
                  <p className="text-[10px] text-text-secondary mt-0.5">
                    {userRole === 'analyst'
                      ? 'Повний доступ до всіх функцій'
                      : userRole === 'developer'
                        ? 'Обмежений доступ з можливістю зміни статусів'
                        : 'Тільки перегляд'}
                  </p>
                </div>
              </div>

              {isAnalyst && (
                <div className="grid grid-cols-3 gap-2">
                  <RoleFeature
                    icon={Move}
                    title="Перетягування міток"
                    description="Alt + перетягування мишею"
                  />
                  <RoleFeature
                    icon={MousePointerClick}
                    title="Створення події"
                    description="ПКМ на макеті"
                  />
                  <RoleFeature
                    icon={Edit3}
                    title="Редагування"
                    description="Зміна всіх подій та екранів"
                  />
                </div>
              )}
            </div>
          )}

          {/* Shortcuts by Category */}
          {groupedShortcuts.map(({ category, shortcuts }) => (
            <ShortcutCategorySection
              key={category}
              category={category}
              shortcuts={shortcuts}
            />
          ))}
        </div>

      </div>
    </div>
  );
}

// Helper component for role features
function RoleFeature({
  icon: Icon,
  title,
  description,
  className = '',
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start gap-2 p-2 bg-surface/50 rounded-lg ${className}`}
    >
      <div className="flex-shrink-0 w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center">
        <Icon size={14} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-text-primary">{title}</p>
        <p className="text-[10px] text-text-secondary mt-0.5">{description}</p>
      </div>
    </div>
  );
}

// Shortcut category section component
function ShortcutCategorySection({
  category,
  shortcuts,
}: {
  category: ShortcutCategory;
  shortcuts: ShortcutDefinition[];
}) {
  if (shortcuts.length === 0) return null;

  const Icon = CATEGORY_ICONS[category];
  const colors = CATEGORY_COLORS[category];

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 ${colors.bg} rounded-md flex items-center justify-center`}>
          <Icon size={12} className={colors.text} />
        </div>
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
          {CATEGORY_LABELS[category]}
        </h3>
        <div className="flex-1 h-px bg-border ml-2" />
      </div>
      <div className="space-y-0.5">
        {shortcuts.map((shortcut, index) => (
          <ShortcutItem key={`${shortcut.key}-${index}`} shortcut={shortcut} />
        ))}
      </div>
    </div>
  );
}

// Individual shortcut item component
function ShortcutItem({ shortcut }: { shortcut: ShortcutDefinition }) {
  return (
    <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-elevated/60 transition-colors group">
      <span className="text-xs text-text-secondary group-hover:text-text-primary transition-colors">
        {shortcut.description}
      </span>
      <ShortcutKeys shortcut={shortcut} />
    </div>
  );
}

// Enhanced shortcut keys display component
function ShortcutKeys({
  shortcut,
}: {
  shortcut: { key: string; ctrl?: boolean; shift?: boolean; alt?: boolean };
}) {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const keys: { key: string; type: 'modifier' | 'main' }[] = [];

  if (shortcut.ctrl) {
    keys.push({ key: isMac ? '⌘' : 'Ctrl', type: 'modifier' });
  }
  if (shortcut.shift) {
    keys.push({ key: isMac ? '⇧' : 'Shift', type: 'modifier' });
  }
  if (shortcut.alt) {
    keys.push({ key: isMac ? '⌥' : 'Alt', type: 'modifier' });
  }

  // Format main key
  let keyName = shortcut.key;
  const upperKey = shortcut.key.toUpperCase();
  if (upperKey === ' ') keyName = 'Space';
  else if (upperKey === 'ARROWLEFT') keyName = '←';
  else if (upperKey === 'ARROWRIGHT') keyName = '→';
  else if (upperKey === 'ARROWUP') keyName = '↑';
  else if (upperKey === 'ARROWDOWN') keyName = '↓';
  else if (upperKey === 'ESCAPE') keyName = 'Esc';
  else if (upperKey === 'ENTER') keyName = 'Enter';
  else if (upperKey === 'DELETE') keyName = 'Del';
  else if (upperKey === 'BACKSPACE') keyName = '⌫';
  else if (upperKey === 'PLUS' || upperKey === '=') keyName = '+';
  else if (upperKey === 'MINUS' || upperKey === '-') keyName = '−';
  else if (upperKey === 'TAB') keyName = 'Tab';
  else if (keyName.length === 1) keyName = keyName.toUpperCase();

  keys.push({ key: keyName, type: 'main' });

  return (
    <div className="flex items-center gap-0.5">
      {keys.map((k, i) => (
        <kbd
          key={i}
          className={`
            inline-flex items-center justify-center
            min-w-[24px] h-6 px-1.5
            text-[10px] font-semibold
            rounded-md
            transition-all duration-200
            ${
              k.type === 'modifier'
                ? 'bg-elevated border border-border text-text-muted shadow-sm'
                : 'bg-gradient-to-b from-surface to-elevated border border-border text-text-primary shadow-md'
            }
          `}
        >
          {k.key}
        </kbd>
      ))}
    </div>
  );
}
